import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader, EmptyState, LoadingShell } from '@/components/ui-bits';
import { fmtRelative } from '@/lib/format';
import { CheckSquare, Plus, X, Calendar, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

function mondayOf(d = new Date()) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PRI_CLASS = {
  urgent: 'border-l-[hsl(var(--danger))]',
  high: 'border-l-[hsl(var(--warning))]',
  normal: 'border-l-primary',
  low: 'border-l-muted-foreground',
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('mine');
  const [view, setView] = useState('calendar'); // calendar | list
  const [weekStart, setWeekStart] = useState(() => mondayOf());
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    title: '', type: 'follow_up', priority: 'normal', due_at: '', assigned_to: '',
    remind_before_hours: 1, email_reminder: true,
  });

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '300' });
      if (filter === 'mine') params.set('mine', 'true');
      if (filter === 'open') params.set('status', 'open');
      const { data } = await api.get(`/tasks?${params}`);
      setTasks(data.tasks || []);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        week_start: weekStart.toISOString(),
        mine: filter === 'mine' ? 'true' : 'false',
      });
      const { data } = await api.get(`/tasks/calendar?${params}`);
      setCalendar(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar calendario');
    } finally {
      setLoading(false);
    }
  }, [weekStart, filter]);

  useEffect(() => {
    if (view === 'calendar') loadCalendar();
    else loadList();
  }, [view, loadCalendar, loadList]);

  useEffect(() => {
    api.get('/admin/users').then((r) => setUsers(r.data.users || [])).catch(() => {});
  }, []);

  const toggle = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: t.status === 'done' ? 'open' : 'done' });
      if (view === 'calendar') loadCalendar();
      else loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...f,
        due_at: f.due_at ? new Date(f.due_at).toISOString() : null,
        assigned_to: f.assigned_to || undefined,
        remind_before_hours: Number(f.remind_before_hours) || 0,
        email_reminder: !!f.email_reminder,
      };
      await api.post('/tasks', payload);
      setOpen(false);
      setF({
        title: '', type: 'follow_up', priority: 'normal', due_at: '', assigned_to: '',
        remind_before_hours: 1, email_reminder: true,
      });
      toast.success('Tarea creada');
      if (view === 'calendar') loadCalendar();
      else loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const shiftWeek = (delta) => {
    const n = new Date(weekStart);
    n.setDate(n.getDate() + delta * 7);
    setWeekStart(n);
  };

  const openCreateOnDay = (dateStr) => {
    setF((prev) => ({ ...prev, due_at: `${dateStr}T10:00` }));
    setOpen(true);
  };

  const weekLabel = useMemo(() => {
    if (!calendar) return '';
    return `${calendar.week_start} → ${calendar.week_end}`;
  }, [calendar]);

  const todayStr = toISODate(new Date());

  return (
    <div>
      <PageHeader
        title="Tareas"
        description="Calendario semanal y lista de seguimientos."
        actions={(
          <div className="flex gap-2">
            <button type="button" className={`btn-ghost ${view === 'calendar' ? 'bg-accent' : ''}`} onClick={() => setView('calendar')}>
              <Calendar className="h-4 w-4" /> Semana
            </button>
            <button type="button" className={`btn-ghost ${view === 'list' ? 'bg-accent' : ''}`} onClick={() => setView('list')}>
              <List className="h-4 w-4" /> Lista
            </button>
            <button data-testid="tasks-new-btn" onClick={() => setOpen(true)} className="btn-primary">
              <Plus className="h-4 w-4" /> Nueva
            </button>
          </div>
        )}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {[['mine', 'Mías'], ['open', 'Abiertas'], ['all', 'Todas']].map(([k, label]) => (
          <button
            key={k}
            data-testid={`tasks-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`btn-ghost ${filter === k ? 'bg-accent text-foreground' : ''}`}
          >
            {label}
          </button>
        ))}
      </div>

      {view === 'calendar' ? (
        <>
          <div className="flex items-center justify-between mb-3">
            <button type="button" className="btn-ghost" onClick={() => shiftWeek(-1)}>
              <ChevronLeft className="h-4 w-4" /> Semana ant.
            </button>
            <div className="text-sm font-medium">
              {weekLabel || '…'}
              <button type="button" className="btn-ghost h-7 px-2 text-xs ml-2" onClick={() => setWeekStart(mondayOf())}>
                Hoy
              </button>
            </div>
            <button type="button" className="btn-ghost" onClick={() => shiftWeek(1)}>
              Semana sig. <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {loading || !calendar ? (
            <LoadingShell />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
              {(calendar.days || []).map((day) => {
                const isToday = day.date === todayStr;
                return (
                  <div
                    key={day.date}
                    className={`surface min-h-[180px] p-2 flex flex-col ${isToday ? 'ring-2 ring-primary/40' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-semibold ${isToday ? 'text-primary' : ''}`}>{day.label}</span>
                      <button
                        type="button"
                        className="btn-ghost h-6 w-6 p-0"
                        title="Nueva tarea este día"
                        onClick={() => openCreateOnDay(day.date)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="space-y-1.5 flex-1">
                      {(day.tasks || []).map((t) => (
                        <div
                          key={t.id}
                          className={`rounded-lg border border-border bg-card p-1.5 text-[11px] border-l-2 ${PRI_CLASS[t.priority] || PRI_CLASS.normal} ${t.status === 'done' ? 'opacity-50' : ''}`}
                        >
                          <label className="flex items-start gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={t.status === 'done'}
                              onChange={() => toggle(t)}
                            />
                            <span className={`flex-1 leading-snug ${t.status === 'done' ? 'line-through' : ''}`}>
                              {t.title}
                            </span>
                          </label>
                          {t.lead_id && (
                            <Link to={`/app/leads/${t.lead_id}`} className="text-primary hover:underline block mt-0.5 truncate pl-4">
                              {t.lead_name || 'Lead'}
                            </Link>
                          )}
                          <div className="text-muted-foreground pl-4 mt-0.5">
                            {(t.due_at || '').slice(11, 16) || '—'} · {t.type}
                          </div>
                        </div>
                      ))}
                      {(day.tasks || []).length === 0 && (
                        <div className="text-[10px] text-muted-foreground text-center py-6">—</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : loading ? (
        <LoadingShell />
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={CheckSquare}
          title="Sin tareas"
          description="Crea tu primera tarea o asigna seguimientos desde un lead."
          cta={<button onClick={() => setOpen(true)} className="btn-primary"><Plus className="h-4 w-4" /> Nueva tarea</button>}
        />
      ) : (
        <div className="surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="px-3 py-2" />
                <th className="px-3 py-2 font-medium">Título</th>
                <th className="px-3 py-2 font-medium">Tipo</th>
                <th className="px-3 py-2 font-medium">Prioridad</th>
                <th className="px-3 py-2 font-medium">Vence</th>
                <th className="px-3 py-2 font-medium">Lead</th>
                <th className="px-3 py-2 font-medium">Asignado</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.id} data-testid="task-row" className="h-10 border-b border-border/70 hover:bg-accent/60">
                  <td className="px-3"><input type="checkbox" checked={t.status === 'done'} onChange={() => toggle(t)} /></td>
                  <td className={`px-3 py-2 ${t.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{t.title}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{t.type}</td>
                  <td className="px-3 py-2"><span className="chip">{t.priority}</span></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{fmtRelative(t.due_at)}</td>
                  <td className="px-3 py-2 text-xs">
                    {t.lead_id ? (
                      <Link to={`/app/leads/${t.lead_id}`} className="text-primary hover:underline">
                        {t.lead_name || 'Ver'}
                      </Link>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{users.find((u) => u.id === t.assigned_to)?.name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={createTask} onClick={(e) => e.stopPropagation()} className="surface p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold">Nueva tarea</h2>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <label>Título<input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="input mt-1" /></label>
              <label>Tipo
                <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="input mt-1">
                  {['call', 'whatsapp', 'email', 'appointment', 'follow_up', 'reminder'].map((x) => <option key={x}>{x}</option>)}
                </select>
              </label>
              <label>Prioridad
                <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="input mt-1">
                  {['low', 'normal', 'high', 'urgent'].map((x) => <option key={x}>{x}</option>)}
                </select>
              </label>
              <label>Asignar a
                <select value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} className="input mt-1">
                  <option value="">Yo</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
              </label>
              <label>Vencimiento<input type="datetime-local" value={f.due_at} onChange={(e) => setF({ ...f, due_at: e.target.value })} className="input mt-1" /></label>
              <label>Recordar
                <select value={f.remind_before_hours} onChange={(e) => setF({ ...f, remind_before_hours: Number(e.target.value) })} className="input mt-1">
                  <option value={0}>Al vencer</option>
                  <option value={1}>1 hora antes</option>
                  <option value={3}>3 horas antes</option>
                  <option value={24}>1 día antes</option>
                  <option value={48}>2 días antes</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.email_reminder} onChange={(e) => setF({ ...f, email_reminder: e.target.checked })} />
                Enviar recordatorio por email
              </label>
            </div>
            <button className="btn-primary w-full mt-4">Crear</button>
          </form>
        </div>
      )}
    </div>
  );
}
